import React from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';
import { ICalendarBase, ICalendar, isRandomCalendar } from '../../../../../entities/calendar';
import { getCalendarFirstFreeSlot } from '../../../../../services/api/calendar';
import CalendarDescription from '../../../../CalendarDescription/CalendarDescription';
import Image from '../../../../Image/Image';
import Popup from '../../../../Popup/Popup';
import globalMessages from 'src/services/globalMessages';
import { useContext, useCallback, useState } from 'react';
import { useSubscription } from 'src/hooks';
import { getServiceFirstFreeSlot } from 'src/services/api/service';
import { IServiceDetails } from 'src/entities/service';
import { Bone, TextSkeleton, ParagraphSkeleton } from 'src/components/Placeholder/Placeholder';
import { ButtonSkeleton } from 'src/components/form-components/Button/Button';
import { AppContext, IAppProvidedState } from 'src/contexts/AppContext';

interface ICalendarItemProps {
    calendar: ICalendar;
    service?: IServiceDetails;
    button: React.ReactElement;
    selectCalendar: (calendar: ICalendarBase) => void;
    withLogoSpace: boolean;
}

const CalendarItem: React.FunctionComponent<ICalendarItemProps> = ({ calendar, service, withLogoSpace, button, selectCalendar }) => {
    const [isLongDescriptionVisible, setIsLongDescriptionVisible] = useState(false);

    const { company } = useContext<IAppProvidedState>(AppContext);

    const fetchFirstFreeSlot = useCallback(async () => {
        if (!company?.ShouldDisplayFirstTime) {
            return;
        }

        if (isRandomCalendar(calendar.Id)) {
            if (!service) {
                return;
            }
            return getServiceFirstFreeSlot(service.Id);
        }

        if (button.props.isFirstStep) {
            return getCalendarFirstFreeSlot(calendar.Id);
        }

        return getCalendarFirstFreeSlot(calendar.Id, service?.Id);
    }, [calendar.Id, company?.ShouldDisplayFirstTime]);

    const [firstFreeSlot, isLoading] = useSubscription(fetchFirstFreeSlot);

    return (
        <div className="calendar-item">
            {calendar.PictureLink
                ? <Image
                    className="calendar-item__calendar-logo"
                    src={calendar.PictureLink}
                />
                : withLogoSpace && <div className="calendar-item__calendar-logo" />
            }
            <header className="calendar-item__header">
                <h3 className="calendar-item__name">
                    {isRandomCalendar(calendar.Id) ?
                        <FormattedMessage {...globalMessages.whichever} />
                        : calendar.Name}
                </h3>
                <div className="calendar-item__first-free-slot">
                    {(firstFreeSlot || isLoading)
                        && <>
                            <span className="calendar-item__first-free-slot-text">
                                <FormattedMessage {...globalMessages.firstFreeSlot} />
                            </span>
                            {isLoading
                                ? <TextSkeleton><FormattedDate value={0} /></TextSkeleton>
                                : <time className="calendar-item__first-free-slot-text calendar-item__first-free-slot-value">
                                    <FormattedDate
                                        value={firstFreeSlot!}
                                        year="numeric"
                                        month="numeric"
                                        day="numeric"
                                        hour="numeric"
                                        minute="numeric" />
                                </time>}
                        </>
                    }
                </div>
            </header>
            {button}
            {
                (calendar.ShortDescription || calendar.Description) &&
                <div className="calendar-item__description">
                    {calendar.ShortDescription &&
                        <p className="calendar-item__description-text p--light-text">{calendar.ShortDescription}</p>
                    }
                    {calendar.Description &&
                        <>
                            <button className="calendar-item__more-description link" onClick={() => setIsLongDescriptionVisible(true)}>
                                <FormattedMessage {...globalMessages.readMore} />
                            </button>
                            <Popup
                                visible={isLongDescriptionVisible}
                                closePopup={() => setIsLongDescriptionVisible(false)}
                                title={calendar.Name}>
                                <CalendarDescription
                                    calendar={calendar}
                                    firstFreeSlot={firstFreeSlot ? new Date(firstFreeSlot) : null}
                                    startReservation={() => selectCalendar(calendar)} />
                            </Popup>
                        </>
                    }
                </div>
            }
        </div >
    );
}

export const CalendarItemSkeleton = () =>
    <div className="calendar-item" role="presentation">
        <Bone className="calendar-item__calendar-logo" />
        <div className="calendar-item__header">
            <div className="calendar-item__name">
                <TextSkeleton>
                    {'Skeleton calendar'}
                </TextSkeleton>
            </div>
            <div className="calendar-item__first-free-slot">
                <TextSkeleton><FormattedMessage {...globalMessages.firstFreeSlot} /><FormattedDate value={0} /></TextSkeleton>
            </div>
        </div>
        <ButtonSkeleton
            className="calendar-item__select-button"
            caption={globalMessages.firstStepAction}
        />
        <div className="calendar-item__description">
            <ParagraphSkeleton lines={2} />
        </div>
    </div>

export default CalendarItem;